import React, { useState, useEffect } from 'react';
import { Minus, X, MoreHorizontal, Phone, PhoneOff } from 'lucide-react';

const CallWidgetV2 = ({
  callInfo,
  status,
  duration,
  onClose,
  onMinimize,
  onReport
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const isDisconnected = status?.toLowerCase().includes('disconnected');

  useEffect(() => {
    let timer;
    if (isDisconnected && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (isDisconnected && countdown === 0) {
      onReport(); // Automatically open ACW when countdown reaches 0
    }
    return () => clearInterval(timer);
  }, [isDisconnected, countdown, onReport]);

  const formatTime = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-2xl flex items-center gap-3 cursor-pointer z-[100]" onClick={() => setIsMinimized(false)}>
        <Phone size={16} className="animate-pulse" />
        <span className="text-sm font-bold">Call with {callInfo.customerName}</span>
        <span className="text-xs opacity-80">{formatTime(duration)}</span>
      </div>
    );
  }

  return (
    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-[100] animate-in fade-in zoom-in duration-300">
      {/* Widget Header */}
      <div className="bg-blue-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-white font-bold text-sm tracking-tight">
            Call Widget ({callInfo.provider || 'Kit19 80'})
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1.5 hover:bg-white/10 rounded-md text-white transition-colors"
          >
            <Minus size={16} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded-md text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="flex justify-end">
          <button className="text-slate-400 hover:text-slate-600 transition-colors">
            <MoreHorizontal size={20} />
          </button>
        </div>

        {/* Participants Section */}
        <div className="flex items-center justify-between px-4">
          {/* Agent */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-slate-100 p-1">
              <img
                src="/assets/custom/img/img_avatar1.png"
                alt="Agent"
                className="w-full h-full rounded-full grayscale opacity-80"
              />
            </div>
            <div className="text-center">
              <p className="text-xs font-bold text-slate-800 truncate max-w-[100px]">{callInfo.agentName}</p>
              <p className="text-[10px] text-slate-500 font-medium">{callInfo.agentNumber}</p>
            </div>
          </div>

          {/* Call Animation */}
          <div className="flex flex-col items-center gap-2 flex-1">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-20" />
              <img
                src="/assets/custom/UserCRMCampaign/Images/callprocess-green.gif"
                alt="Calling"
                className="w-12 h-12 relative z-10"
              />
            </div>
          </div>

          {/* Customer */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-slate-100 p-1">
              <img
                src="/assets/custom/img/img_avatar1.png"
                alt="Customer"
                className="w-full h-full rounded-full grayscale opacity-80"
              />
            </div>
            <div className="text-center">
              <p className="text-xs font-bold text-slate-800 truncate max-w-[100px]">{callInfo.customerName}</p>
              <p className="text-[10px] text-slate-500 font-medium">{callInfo.customerNumber}</p>
            </div>
          </div>
        </div>

        {/* Status & Timer */}
        <div className="text-center space-y-1 py-4 bg-slate-50/50 rounded-2xl border border-slate-100/50">
          <p className="text-lg font-bold text-slate-800">{status}</p>
          <p className="text-sm font-mono font-bold text-blue-600">{formatTime(duration)}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-800 px-6 py-4 min-h-[60px] flex items-center justify-center">
        {isDisconnected ? (
          <button
            onClick={onReport}
            className="w-full py-3 text-white font-bold bg-slate-700 hover:bg-slate-600 rounded-lg transition-all animate-in slide-in-from-bottom-2 duration-300"
          >
            Closing In <span className="text-sky-400">{countdown}</span> Sec
          </button>
        ) : (
          <p className="text-white/80 text-center text-sm font-medium">
            Call Disconnected? {' '}
            <button
              onClick={onReport}
              className="text-blue-400 hover:text-blue-300 font-bold underline underline-offset-4 decoration-2 transition-colors"
            >
              Click to report
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default CallWidgetV2;
